import React, { useState } from 'react';
import { DocumentUpload } from '../specifications/DocumentUpload';
import { Button } from '../../../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { Upload } from 'lucide-react';
import { SpecificationDocument } from '../../../types';
import { AddSpecification, getPreSignedUrlToUpload } from '../../../../../redux/Project/specificationSaga';
import customNotification from '../../../../../components/ui/custom-notification';

interface DocumentUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpload: (files: FileList | null) => void;
    title?: string;
    acceptedTypes?: string;
    multiple?: boolean;
    maxFiles?: number;
    buttonText?: string;
    label?: string;
    description?: string;
    required?: boolean;
    spec?: SpecificationDocument;
    documentType?: string;
}

export const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
    isOpen,
    onClose,
    onUpload, 
    title = 'Upload Documents',
    acceptedTypes = '.pdf,.doc,.docx,.xlsx,.xls',
    multiple = false,
    maxFiles = 1,
    buttonText = 'Upload Documents',
    label = 'File',
    description = 'Upload the specification documents (PDF, Excel, DOC, etc...)',
    required = true,
    spec,
    documentType = 'NDT'
}) => {
    
    const [files, setFiles] = useState<File[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedTags, setSelectedTags] = useState<{ [fileIndex: number]: string[] }>({});
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    const handleFileSelect = (files: FileList | null) => {
        if (files && files.length > 0) {
            const fileArray = Array.from(files);
            setFiles(fileArray);
      
            // For multiple files, we don't need to set a single file in newSpecification
            // The files array will be used directly in the upload process
          }
    };

    const handleTagsChange = (fileIndex: number, tags: string[]) => {
        setSelectedTags(prev => ({
            ...prev,
            [fileIndex]: tags
        }));
    };
   
    const handleUpload = async () => {
        if (files) {
            setIsUploading(true);
            const payload = {
                pipeId: Number(spec?.pipeId),
                projectId: Number(spec?.projectId),
                projectTypeId: Number(spec?.projectTypeId),
                type: documentType === 'Coating' ? 'COATING' : documentType === 'Welding' ? 'WELDING' : documentType === 'Hydro' ? 'HYDRO' : documentType === 'Torquing' ? 'TORQUING' : documentType,
            }
            const s3Response = await getPreSignedUrlToUpload(files, payload, spec?.version ? spec.version +1 : 1);

            if (s3Response && s3Response.length > 0 ) {
                const newDoc = {
                    // id: Math.random().toString(36).substr(2, 9),
                    //  fileName: files.length > 0 ? files[0].name : '',
                    type: documentType === 'Coating' ? 'COATING' : documentType === 'Welding' ? 'WELDING' : documentType === 'Hydro' ? 'HYDRO' : documentType === 'Torquing' ? 'TORQUING' : documentType,
                    pipeId: Number(spec?.pipeId),
                    projectId: spec?.projectId,
                    projectTypeId: spec?.projectTypeId,
                    // version: 1,
                    files: s3Response.map((file: any,index:number) => ({
                      //  version: spec?.version ? spec.version + index + 1 : 1,
                        fileName: file.fileName,
                        s3Key: file.s3Key,
                        tags: selectedTags[index] ? selectedTags[index].map((tag: string) => Number(tag)) : []
                      //  docRef: spec?.docRefId || ''
                    })),
                    uploadDate: new Date().toISOString(),
                    uploadedBy:user.email || 'no email'
                };

                const response = await AddSpecification([newDoc], false)
                if (response && response.data && response.data.length > 0) {
                    const documentTypeLabel =
                      documentType === 'Coating' ? 'Coating'
                        : documentType === 'Welding' ? 'Welding'
                          : documentType === 'Hydro' ? 'Hydro'
                            : documentType === 'Torquing' ? 'Torquing'
                              : 'NDT';
                    customNotification.success('Document uploaded successfully', `The ${documentTypeLabel} document has been uploaded successfully.`);
                   // onUpload(files as unknown as FileList);
                   onUpload(response.data);
                    setFiles([]);
                    setSelectedTags({});
                    setIsUploading(false);
                    onClose();
                }
            }
        }   
    };

    const handleClose = () => {
        setFiles([]);
        setSelectedTags({});
        setIsUploading(false);
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="w-[90vw] max-w-[800px] max-h-[90vh] flex flex-col">
                <DialogHeader className="flex-shrink-0 pr-14">
                    <div className="flex items-center justify-between gap-4">
                        <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
                            {title}
                        </DialogTitle>
                        <Button
                            onClick={handleUpload}
                            disabled={!files || files.length === 0 || isUploading}
                            loading={isUploading}
                            loadingText="Uploading..."
                            className="flex items-center gap-2 bg-hero-red hover:bg-hero-red-60 text-white"
                        >
                            <Upload className="h-4 w-4" />
                            Upload
                        </Button>
                    </div>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto p-2 overflow-x-hidden">
                <div className="py-4" >
                    <DocumentUpload
                        onUpload={handleFileSelect}
                        acceptedTypes={acceptedTypes}
                        multiple={multiple}
                        maxFiles={maxFiles}
                        buttonText={buttonText}
                        label={label}
                        description={description}
                        required={required}
                        className="w-full"
                        enableNDTValidation={true}
                        enableTagging={true}
                        specificationType={documentType}
                        projectId={spec?.projectId}
                        onTagsChange={handleTagsChange}
                        selectedTags={selectedTags}
                      //  ndtTechnique={spec?.productionSheet?.ndtTechnique}
                    />
                </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}; 